import React, { useState } from 'react';
import { Download, Bell, Share } from 'lucide-react';
import BrandLogo from '../brand/BrandLogo';

/**
 * Deterministic buyer onboarding prompt (Jun 2026).
 *
 * Shown at an appropriate, non-blocking point in the buyer journey (never on the
 * very first paint) with the APPROVED copy and exactly two choices. There is NO
 * "Maybe Later" button. Option A uses the REAL PWA install mechanism (shared with
 * the seller PWA). Option B persists a collection-notification preference and
 * requests browser push permission — install and permission stay separate.
 *
 * Props:
 *   open            : boolean
 *   installPrompt   : the captured beforeinstallprompt event (or null)
 *   onInstall()     : async → runs native install, resolves to 'installed' | 'dismissed' | 'unsupported'
 *   onNotify()      : async → persists preference + requests push permission
 *   onClose()       : hide for this session (not a decision)
 */
export default function BuyerOnboardingPrompt({ open, installPrompt, onInstall, onNotify, onClose }) {
  const [busy, setBusy] = useState('');
  const [iosHint, setIosHint] = useState(false);
  if (!open) return null;

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

  const doInstall = async () => {
    setBusy('install');
    const res = await onInstall();
    setBusy('');
    if (res === 'unsupported' && isIOS) setIosHint(true);
  };
  const doNotify = async () => {
    setBusy('notify');
    await onNotify();
    setBusy('');
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" data-testid="buyer-onboarding-prompt">
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl p-6 relative animate-in slide-in-from-bottom duration-300">
        <div className="flex items-center gap-2 mb-3">
          <BrandLogo className="h-7 w-auto" />
        </div>
        <h2 className="text-[18px] font-bold text-neutral-900" data-testid="buyer-onboarding-title">
          Stay updated with your orders &amp; new collections
        </h2>
        <p className="mt-2 text-[13.5px] text-neutral-600 leading-relaxed">
          To receive shipping details, track your order, and get new collection notifications, download FABTEXS AI to your Home Screen.
        </p>

        {iosHint && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-neutral-50 border border-neutral-200 p-3 text-[12.5px] text-neutral-700" data-testid="buyer-ios-install-hint">
            <Share className="w-4 h-4 mt-0.5 shrink-0 text-[#075E54]" />
            <span>On iPhone/iPad: tap the <b>Share</b> icon, then <b>Add to Home Screen</b>.</span>
          </div>
        )}

        <div className="mt-5 space-y-2.5">
          <button onClick={doInstall} disabled={!!busy} data-testid="buyer-download-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#075E54] text-white font-semibold text-[14px] py-3 hover:bg-[#0a6d61] disabled:opacity-60">
            <Download className="w-4 h-4" /> {busy === 'install' ? 'Opening…' : 'Download FABTEXS AI'}
          </button>
          <button onClick={doNotify} disabled={!!busy} data-testid="buyer-notify-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-neutral-300 text-neutral-800 font-semibold text-[14px] py-3 hover:bg-neutral-50 disabled:opacity-60">
            <Bell className="w-4 h-4" /> {busy === 'notify' ? 'Saving…' : 'Only Notify Me About New Collections'}
          </button>
        </div>
      </div>
    </div>
  );
}
